import { useState, useEffect } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface PkgInfo { path: string; dependencies: Record<string, string>; devDependencies: Record<string, string> }

export function PackagePanel() {
  const [packages, setPackages] = useState<PkgInfo[]>([])
  const [search, setSearch] = useState('')
  const [installing, setInstalling] = useState(false)
  const [status, setStatus] = useState('')

  const refresh = () => {
    authFetch(apiUrl('/api/tools/packages')).then((r) => r.json()).then(setPackages).catch(() => {})
  }
  useEffect(refresh, [])

  const install = async (dev = false) => {
    if (!search.trim()) return
    const pkgName = search.trim()

    // Check if already installed
    const alreadyInstalled = packages.some((pkg) => 
      pkgName in pkg.dependencies || pkgName in pkg.devDependencies
    )
    if (alreadyInstalled) {
      if (!window.confirm(`"${pkgName}" is already installed. Reinstall/update it?`)) return
    }

    setInstalling(true)
    setStatus(`Installing ${pkgName}...`)
    const cwd = packages[0]?.path !== '.' ? packages[0]?.path : undefined
    const res = await authFetch(apiUrl('/api/tools/packages/install'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: search.trim(), dev, cwd }),
    })
    const data = await res.json()
    setStatus(data.ok ? `Installed ${search}` : data.error ?? 'Failed')
    setSearch('')
    setInstalling(false)
    refresh()
    setTimeout(() => setStatus(''), 3000)
  }

  const uninstall = async (name: string, cwd?: string) => {
    setStatus(`Removing ${name}...`)
    await authFetch(apiUrl('/api/tools/packages/uninstall'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, cwd }),
    })
    setStatus(`Removed ${name}`)
    refresh()
    setTimeout(() => setStatus(''), 3000)
  }

  return (
    <div style={panelStyle}>
      <div style={headerStyle}><span>PACKAGES</span></div>

      <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') install() }} placeholder="Package name..." style={inputStyle} disabled={installing} />
          <button onClick={() => install()} disabled={installing || !search.trim()} style={btnStyle}>Add</button>
          <button onClick={() => install(true)} disabled={installing || !search.trim()} style={{ ...btnStyle, color: 'var(--text-2)', borderColor: 'var(--border-2)' }}>Dev</button>
        </div>
        {status && <div style={{ fontSize: 9, color: 'var(--accent)', fontFamily: 'var(--font-mono)', marginTop: 4 }}>{status}</div>}
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {packages.length === 0 ? (
          <div style={emptyStyle}>
            <span style={{ fontSize: 24 }}>📦</span>
            <span>No package.json found</span>
          </div>
        ) : packages.map((pkg) => (
          <div key={pkg.path}>
            {Object.keys(pkg.dependencies).length > 0 && (
              <>
                <div style={sectionStyle}>Dependencies ({pkg.path})</div>
                {Object.entries(pkg.dependencies).map(([name, ver]) => (
                  <div key={name} style={pkgRow}>
                    <span style={{ color: 'var(--text-0)' }}>{name}</span>
                    <span style={{ fontSize: 9, color: 'var(--text-2)' }}>{ver}</span>
                    <button onClick={() => uninstall(name, pkg.path !== '.' ? pkg.path : undefined)} style={delBtn}>
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                ))}
              </>
            )}
            {Object.keys(pkg.devDependencies).length > 0 && (
              <>
                <div style={sectionStyle}>Dev Dependencies</div>
                {Object.entries(pkg.devDependencies).map(([name, ver]) => (
                  <div key={name} style={pkgRow}>
                    <span style={{ color: 'var(--text-1)' }}>{name}</span>
                    <span style={{ fontSize: 9, color: 'var(--text-2)' }}>{ver}</span>
                    <button onClick={() => uninstall(name, pkg.path !== '.' ? pkg.path : undefined)} style={delBtn}>
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const panelStyle: React.CSSProperties = { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', borderRight: '1px solid var(--border)', overflow: 'hidden' }
const headerStyle: React.CSSProperties = { padding: '8px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', flexShrink: 0 }
const inputStyle: React.CSSProperties = { flex: 1, padding: '5px 8px', borderRadius: 4, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 11, outline: 'none' }
const btnStyle: React.CSSProperties = { padding: '4px 10px', borderRadius: 4, border: '1px solid var(--accent)', background: 'rgba(124,110,245,0.08)', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, flexShrink: 0 }
const emptyStyle: React.CSSProperties = { padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: 'var(--text-2)', fontSize: 11, fontFamily: 'var(--font-mono)' }
const sectionStyle: React.CSSProperties = { padding: '6px 14px', fontSize: 9, fontWeight: 700, color: 'var(--text-2)', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }
const pkgRow: React.CSSProperties = { padding: '3px 14px 3px 20px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontFamily: 'var(--font-mono)' }
const delBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', marginLeft: 'auto', display: 'flex', padding: 2 }
